import { reactive } from 'vue';
/**
 * Cadence (ms) at which each auth store re-checks the current session — refreshing a token that is
 * about to expire and re-emitting `authenticated` for a still-valid one. A trade-off between how
 * quickly a revoked or expired session is noticed and how much idle work the app does; it must stay
 * well below the expiration skew used when deciding that a token needs refreshing.
 */
const AUTH_CHECK_INTERVAL_MS = 10_000;
/**
 * Assembles an `AuthStore` from its session and its per-context operations: exposes the shared
 * reactive surface every consumer reads and starts the periodic re-check. The result is typed by
 * inference rather than annotated as `AuthStore`, because Vue's `UnwrapNestedRefs` cannot prove a
 * bare generic survives it; each store's own `implements: AuthStore` is what enforces the surface.
 *
 * It exists so the two stores cannot drift on the parts that must not differ — most importantly the
 * re-check poll, whose absence would leave a session silently un-renewed until the next navigation.
 *
 * Deliberately hook-free: no strategy callbacks, no branches. The stores are different state
 * machines that share scaffolding, not one algorithm with variations — a future urge to add a hook
 * here is the signal that the change belongs in a store instead.
 *
 * @__NO_SIDE_EFFECTS__
 */
export function createAuthStore(options) {
    const { session, ...operations } = options;
    setInterval(() => void operations.checkAuth(), AUTH_CHECK_INTERVAL_MS);
    // The session's members are assigned as the raw refs they are: `reactive()` keeps them live,
    // whereas reading `.value` here would freeze each one at setup time — silently, with no type
    // error. See the contract documented on `AuthSession`.
    return reactive({
        auth: session.auth,
        user: session.user,
        events: session.events,
        ...operations,
    });
}
//# sourceMappingURL=createAuthStore.js.map